import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PenLine, MonitorSmartphone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { RequestMemberSignatureButton } from './RequestMemberSignatureButton';
import { paperLine, isWetRequired, resolveSigningChoice, type SigningChoice } from './signingModel';

interface DocumentSigningControlProps {
  doc: any;
  unaId?: string | null;
  /** True when the Association keeps a seal. Controls the paper line wording. */
  sealEnabled?: boolean;
  onChanged?: () => void;
}

/**
 * One small control per paper. The steward picks the portal or paper, and the
 * pick is saved with the document so it stays picked.
 */
export function DocumentSigningControl({ doc, unaId, onChanged, sealEnabled }: DocumentSigningControlProps) {
  const { toast } = useToast();
  const meta = doc?.merge_field_data || {};
  const [choice, setChoice] = useState<SigningChoice>(resolveSigningChoice(meta));
  const [saving, setSaving] = useState(false);

  const wetRequired = isWetRequired(meta);
  const reason: string | undefined = meta.signing_reason;

  if (wetRequired) {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center text-sm text-muted-foreground">
          <PenLine className="h-4 w-4 mr-2" />
          Sign this one by hand, in blue ink
        </span>
        {reason && <p className="text-sm text-muted-foreground">{reason}</p>}
      </div>
    );
  }

  async function pick(next: SigningChoice) {
    if (next === choice) return;
    setChoice(next);
    setSaving(true);
    try {
      const { error } = await supabase
        .from('generated_documents')
        .update({ merge_field_data: { ...meta, signing_choice: next } as any })
        .eq('id', doc.id);
      if (error) throw error;
      onChanged?.();
    } catch (error: any) {
      toast({
        title: 'We could not save that choice',
        description: error.message || 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={choice === 'portal' ? 'default' : 'outline'}
          className="min-h-11"
          disabled={saving}
          onClick={() => pick('portal')}
        >
          <MonitorSmartphone className="h-4 w-4 mr-2" />
          Sign in the portal
        </Button>
        <Button
          type="button"
          size="sm"
          variant={choice === 'paper' ? 'default' : 'outline'}
          className="min-h-11"
          disabled={saving}
          onClick={() => pick('paper')}
        >
          <PenLine className="h-4 w-4 mr-2" />
          Sign on paper, blue ink
        </Button>
      </div>
      {reason && <p className="text-sm text-muted-foreground">{reason}</p>}
      {choice === 'portal' ? (
        unaId ? (
          <RequestMemberSignatureButton
            documentId={doc.id}
            unaId={unaId}
            documentName={doc.document_name}
            onSignaturesRequested={onChanged}
          />
        ) : null
      ) : (
        <p className="text-sm text-muted-foreground">{paperLine(sealEnabled)}</p>
      )}
    </div>
  );
}

export default DocumentSigningControl;
